import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import GameHeader from "./game-header"
import AnswerButton from "./answer-button"
import ScoringButtons from "./scoring-buttons"
import CheckpointModal from "./checkpoint-modal"
import Flashcard from "./flashcard"
import type { Flashcard as FlashcardType } from "@/services/api/flashcards/get"
import type { SpecialDeck } from "@/services/api/flashcards/get-special-deck"
import NewCardLabel from "./new-card-label"
import SpecialDeckCheckpointModal from "./special-deck-checkpoint-modal"
import mixpanelClient from "@/services/mixpanel"
import { useUser } from "@/modules/user/stores"

type GameDataFlashcard = FlashcardType | SpecialDeck
interface GameLayoutProps {
  gameData: {
    name: string
    flashcards: GameDataFlashcard[]
  }
}

export default function GameLayout({ gameData }: GameLayoutProps) {
  const { user } = useUser()
  const navigate = useNavigate()
  const flipRef = useRef<() => void>(() => {})
  const { flashcards, name } = gameData

  const [allCards, setAllCards] = useState(flashcards)
  const [currentCards, setCurrentCards] = useState(allCards.slice(0, 10))
  const [selectedCard, setSelectedCard] = useState(currentCards.at(0))

  const [showAnswerButton, setShowAnswerButton] = useState<boolean>(true)
  const [showScoringButtons, setShowScoringButtons] = useState<boolean>(false)
  const [showCheckpointModal, setShowCheckpointModal] = useState<boolean>(false)
  const showAnswerButtonRef = useRef<boolean>(true)

  const [confidenceLevels, setConfidenceLevels] = useState<number[]>([])
  const selectedCardIndex = currentCards.findIndex((card) => card.id === selectedCard?.id) + 1
  const [currentCardIndexInTheDeck, setCurrentCardIndexInTheDeck] = useState(1)

  useEffect(() => {
    const index = flashcards.findIndex((card) => card.id === selectedCard?.id) + 1
    setCurrentCardIndexInTheDeck(index)
  }, [selectedCard?.id, flashcards])

  useEffect(() => {
    showAnswerButtonRef.current = showAnswerButton
  }, [showAnswerButton])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const canContinue = event.code === "Space" || event.key === " " || event.code === "ArrowRight"
      if (!canContinue || !showAnswerButtonRef.current) return
      onClickAnswerButton()
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const onClickAnswerButton = () => {
    flipRef.current()
    setShowScoringButtons(true)
    setShowAnswerButton(false)
  }

  const onClickScoringButton = (confidenceLevel: number) => {
    setConfidenceLevels([...confidenceLevels, confidenceLevel])

    const nextCardIndex = currentCards.findIndex((card) => card.id === selectedCard?.id)
    const newSelectedCard = currentCards[nextCardIndex + 1]

    flipRef.current()
    setShowScoringButtons(false)

    if (!newSelectedCard) {
      setAllCards(allCards.slice(10))
      setShowCheckpointModal(true)
    } else {
      setSelectedCard(newSelectedCard)
      setShowAnswerButton(true)
    }
  }

  const onClickCheckpointButton = () => {
    if (allCards.length === 0) {
      mixpanelClient.flashcards.completeFlashcard({
        completeDate: new Date().toISOString(),
        userPlan: user.activePlan.name,
        userName: user.name,
        userEmail: user.email,
      })
      navigate("/flashcards")
    } else {
      mixpanelClient.flashcards.moreFlashcards({
        userPlan: user.activePlan.name,
        userName: user.name,
        userEmail: user.email,
      })
      const newCards = allCards.slice(0, 10)
      setCurrentCards(newCards)
      setSelectedCard(newCards.at(0))
      setShowAnswerButton(true)
      setShowCheckpointModal(false)
    }
  }

  const isSpecialDeck = (card: GameDataFlashcard): card is SpecialDeck => {
    return "timesViewed" in card
  }

  return (
    <>
      <div className="flex h-full w-full flex-col items-center md:h-auto">
        <GameHeader
          currentQuestionNumber={selectedCardIndex}
          numberOfQuestions={currentCards.length}
          isDisabled={false}
          currentCardIndexInTheDeck={currentCardIndexInTheDeck}
          totalCardsAmount={flashcards.length}
          name={name}
        />

        <div className="mx-auto w-full max-w-[800px] p-3">
          {selectedCard && isSpecialDeck(selectedCard) && selectedCard.timesViewed === 0 && (
            <NewCardLabel />
          )}

          <div className="h-auto w-full">
            <Flashcard
              question={selectedCard?.question ?? ""}
              answer={selectedCard?.answer ?? ""}
              flipRef={flipRef}
            />
          </div>
        </div>
      </div>

      <AnswerButton showAnswerButton={showAnswerButton} onClick={onClickAnswerButton} />

      <ScoringButtons
        showScoringButtons={showScoringButtons}
        onClick={onClickScoringButton}
        cardId={selectedCard?.id ?? ""}
      />

      {selectedCard && isSpecialDeck(selectedCard) ? (
        <SpecialDeckCheckpointModal
          showCheckpointModal={showCheckpointModal}
          onClick={onClickCheckpointButton}
          confidenceLevels={confidenceLevels}
          availableCardsQuantity={allCards.length >= 10 ? 10 : allCards.length}
          totalAmountOfCards={flashcards.length}
        />
      ) : (
        <CheckpointModal
          showCheckpointModal={showCheckpointModal}
          onClick={onClickCheckpointButton}
          confidenceLevels={confidenceLevels}
          availableCardsQuantity={allCards.length >= 10 ? 10 : allCards.length}
          totalAmountOfCards={flashcards.length}
        />
      )}
    </>
  )
}
